import { useCallback } from 'react';
import { openCanvaEditorDirect } from '@/utils/canvaApi';
import { getPrimaryOriginalUrl } from '@/utils/canvasCropping';
import { getViewportCropUrl, validateImageUrlForEditor } from '../utils/cropUtils';
import { getCanvaDimensions } from '../config/dimensions';



export const useCanvaIntegration = (
  editorState: any,
  asset: any,
  imageRef: React.RefObject<HTMLImageElement>,
  secondaryImageRef: React.RefObject<HTMLImageElement>,
  toast: any,
  isCanvaLoading: { type: 'primary' | 'secondary' | null; message: string },
  setIsCanvaLoading: (state: { type: 'primary' | 'secondary' | null; message: string }) => void
) => {
  // Priority image URL functions
  const getPrimaryDisplayImageUrl = useCallback((asset: any): string => {
    // PRIORITY 1: Show markup URL when in markup mode AND markup exists (highest priority)
    if (editorState.primaryImageMode === 'markup' && asset.primary_markup_url) {
      return asset.primary_markup_url;
    }
    
    // PRIORITY 2: When in crop mode, ALWAYS use original URL (pan/zoom applied via CSS transforms)
    if (editorState.primaryImageMode === 'crop') {
      // Check if we have primary image data from editor state (version switching)
      if (editorState.primaryImageData?.imageUrl) {
        return editorState.primaryImageData.imageUrl;
      }
      
      // Use the original URL (crop coordinates handled by CSS transforms)
      const originalUrl = getPrimaryOriginalUrl(asset);
      if (originalUrl) {
        // Validate the URL before returning it
        try {
          validateImageUrlForEditor(originalUrl, 'primary image display');
        } catch (error) {
          // Return empty string to prevent rendering invalid URL
          return '';
        }
        return originalUrl;
      }
    }
    
    // Final fallback
    return asset.primary_image || '';
  }, [editorState.primaryImageMode, editorState.primaryImageData]);

  const getSecondaryDisplayImageUrl = useCallback((asset: any): string => {
    // PRIORITY 1: Show markup URL when in markup mode AND markup exists (highest priority)
    if (editorState.secondaryImageMode === 'markup' && asset.secondary_markup_url) {
      return asset.secondary_markup_url;
    }
    
    // PRIORITY 2: When in crop mode, ALWAYS use original URL (pan/zoom applied via CSS transforms)
    if (editorState.secondaryImageMode === 'crop') {
      // Check if we have secondary image data from editor state (version switching)
      if (editorState.secondaryImageData?.imageUrl) {
        return editorState.secondaryImageData.imageUrl;
      }
      
      // Use the original URL (crop coordinates handled by CSS transforms)
      const originalUrl = asset.secondary_original_url || asset.secondary_image || '';
      if (originalUrl) {
        // Validate the URL before returning it
        try {
          validateImageUrlForEditor(originalUrl, 'secondary image display');
        } catch (error) {
          // Return empty string to prevent rendering invalid URL
          return '';
        }
        return originalUrl;
      }
    }
    
    // Final fallback
    const fallbackUrl = asset.secondary_original_url || asset.secondary_image || '';
    return fallbackUrl;
  }, [editorState]);

  const handleCanvaEditWithCrop = useCallback(async (imageType: 'primary' | 'secondary') => {
    setIsCanvaLoading({ type: imageType, message: 'Preparing image...' });
    
    try {
      let originalImageUrl = '';
      let zoomCoordinates;
      let imageElement;
      
      if (imageType === 'primary') {
        // If in markup mode and markup URL exists, use that directly for Canva editing
        if (editorState.primaryImageMode === 'markup' && asset.primary_markup_url) {
          originalImageUrl = asset.primary_markup_url;
        } else {
          // Always use the true original URL, never the cropped version from editor state
          originalImageUrl = asset.primary_original_url || 
                            asset.primary_image || '';
          // Validate that we have a compatible URL
          validateImageUrlForEditor(originalImageUrl, 'primary Canva edit');
        }
        zoomCoordinates = editorState.zoomCoordinates;
        imageElement = imageRef.current;
      } else {
        // If in markup mode and markup URL exists, use that directly for Canva editing
        if (editorState.secondaryImageMode === 'markup' && asset.secondary_markup_url) {
          originalImageUrl = asset.secondary_markup_url;
        } else {
          // Always use the true original URL, never the cropped version from editor state
          originalImageUrl = asset.secondary_original_url || 
                            asset.secondary_image || '';
          // Validate that we have a compatible URL
          validateImageUrlForEditor(originalImageUrl, 'secondary Canva edit');
        }
        zoomCoordinates = editorState.secondaryZoomCoordinates;
        imageElement = secondaryImageRef.current;
      }

      if (!originalImageUrl) {
        setIsCanvaLoading({ type: null, message: '' });
        toast({
          title: `No ${imageType} image available`,
          description: `This asset doesn't have a ${imageType} original image to edit.`,
          variant: "destructive",
        });
        return;
      }

      if (!imageElement) {
        setIsCanvaLoading({ type: null, message: '' });
        toast({
          title: "Image not loaded",
          description: `${imageType} image element not ready for cropping.`,
          variant: "destructive",
        });
        return;
      }

      // Use the same crop URL that would be saved and displayed in debug panel
      const isMarkupUrl = originalImageUrl.includes('collections/markup');
      let finalUrl = originalImageUrl;
      
      if (!isMarkupUrl && imageElement && zoomCoordinates) {
        // Generate the crop URL using the same logic as save handlers and debug panel
        finalUrl = getViewportCropUrl(
          originalImageUrl,
          zoomCoordinates,
          undefined,
          imageElement
        ) || originalImageUrl;
        
      }

      setIsCanvaLoading({ type: imageType, message: 'Creating Canva design...' });
      
      setTimeout(async () => {
        try {
          const canvaDimensions = getCanvaDimensions(editorState.selectedAspectRatio, editorState.comparisonMode);
          const canvaOptions = { 
            dimensions: canvaDimensions, 
            imageType, 
            mode: editorState[imageType === 'primary' ? 'primaryImageMode' : 'secondaryImageMode'],
            redirect: editorState.config?.redirect
          };
          
          await openCanvaEditorDirect(asset, finalUrl, toast, canvaOptions);
          setIsCanvaLoading({ type: imageType, message: 'Redirecting to Canva...' });
          
          setTimeout(() => {
            setIsCanvaLoading({ type: null, message: '' });
          }, 2000);
        } catch (error) {
          setIsCanvaLoading({ type: null, message: '' });
          throw error;
        }
      }, 500);
    } catch (error) {
      setIsCanvaLoading({ type: null, message: '' });
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Check for specific auth errors that need re-authentication
      const isTokenExpired = errorMessage.includes('canva_token_expired') || 
                           errorMessage.includes('Token refresh failed') ||
                           errorMessage.includes('Canva authorization has expired');
      
      const isNotConnected = errorMessage.includes('canva_not_connected') ||
                            errorMessage.includes('No Canva authorization found');
      
      const isGenericAuthError = errorMessage.includes('Canva authentication expired') ||
                               errorMessage.includes('authentication required') ||
                               errorMessage.includes('session has expired');
      
      if (isTokenExpired || isGenericAuthError) {
        toast({
          title: "🔐 Canva Authentication Expired",
          description: "Your Canva connection has expired. Please go to Collections page and click 'Update Canva Token' to reconnect.",
          variant: "destructive",
          duration: 12000,
          action: {
            label: "Reconnect to Canva",
            onClick: () => window.location.href = '/collections?canva_reauth=true'
          }
        });
      } else if (isNotConnected) {
        toast({
          title: "🎨 Connect to Canva",
          description: "You need to connect to Canva first. Please go to Collections page and connect your Canva account.",
          variant: "destructive",
          duration: 12000,
          action: {
            label: "Connect to Canva", 
            onClick: () => window.location.href = '/collections?canva_connect=true'
          }
        });
      } else {
        // Generic error handling
        toast({
          title: "Error Opening Canva",
          description: `Failed to open Canva editor: ${errorMessage}`,
          variant: "destructive",
          duration: 8000,
        });
      }
    }
  }, [editorState, asset, imageRef, secondaryImageRef, toast, setIsCanvaLoading]);

  return {
    getPrimaryDisplayImageUrl,
    getSecondaryDisplayImageUrl,
    handleCanvaEditWithCrop
  };
};